import { useEffect, useRef, useState } from "react";
import { X, Upload, Trash2, ImageIcon, Video, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { getApiBase } from "../lib/apiBase";
import { compressImage, formatSize } from "./compressImage";

export interface MediaObject {
  key: string;
  size: number;
  uploaded: string;
  url: string;
}

interface MediaLibraryProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (url: string) => void;
}

const TOKEN_KEY = "doudou_admin_token";

async function authedFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const token = localStorage.getItem(TOKEN_KEY);
  const base = getApiBase().replace(/\/$/, '');
  const url = `${base}${path}`;
  return fetch(url, {
    ...init,
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...init.headers,
    },
  });
}

export default function MediaLibrary({ isOpen, onClose, onSelect }: MediaLibraryProps) {
  const [media, setMedia] = useState<MediaObject[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState<{ type: "ok" | "err"; text: string } | null>(null);
  const [compressInfo, setCompressInfo] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) loadMedia();
  }, [isOpen]);

  const notify = (type: "ok" | "err", text: string) => {
    setMessage({ type, text });
    window.setTimeout(() => setMessage(null), 3000);
  };

  const loadMedia = async () => {
    setLoading(true);
    try {
      const res = await authedFetch("/api/media");
      const data = (await res.json()) as { objects?: MediaObject[]; error?: string };
      if (!res.ok) throw new Error(data.error || "載入失敗");
      setMedia(
        (data.objects || []).sort(
          (a, b) => new Date(b.uploaded).getTime() - new Date(a.uploaded).getTime()
        )
      );
    } catch (e) {
      notify("err", e instanceof Error ? e.message : "載入媒體庫失敗");
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    setMessage(null);
    setCompressInfo(null);

    try {
      for (const file of Array.from(files)) {
        let uploadFile = file;

        if (file.type.startsWith("image/")) {
          try {
            const result = await compressImage(file);
            uploadFile = result.file;
            setCompressInfo(
              `已壓縮 ${result.ratio}（${formatSize(result.originalSize)} → ${formatSize(result.compressedSize)}）`
            );
          } catch {
            uploadFile = file;
          }
        }

        const key = `${Date.now()}-${uploadFile.name}`;
        const res = await authedFetch(`/api/media?key=${encodeURIComponent(key)}`, {
          method: "POST",
          headers: { "Content-Type": uploadFile.type || "application/octet-stream" },
          body: await uploadFile.arrayBuffer(),
        });
        const data = (await res.json()) as { error?: string };
        if (!res.ok) throw new Error(data.error || `${file.name} 上傳失敗`);
      }
      notify("ok", "上傳成功");
      await loadMedia();
    } catch (e) {
      notify("err", e instanceof Error ? e.message : "上傳失敗");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDelete = async (key: string) => {
    if (!window.confirm("確定刪除這個媒體？")) return;
    try {
      const res = await authedFetch(`/api/media?key=${encodeURIComponent(key)}`, { method: "DELETE" });
      const data = (await res.json()) as { error?: string };
      if (!res.ok) throw new Error(data.error || "刪除失敗");
      notify("ok", "刪除成功");
      await loadMedia();
    } catch (e) {
      notify("err", e instanceof Error ? e.message : "刪除失敗");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4">
      <div className="flex max-h-[90vh] w-full max-w-4xl flex-col rounded-2xl bg-deep-navy shadow-2xl border border-neon-cyan/20">
        <div className="flex items-center justify-between border-b border-neon-cyan/15 p-4">
          <h2 className="flex items-center gap-2 text-lg font-bold text-white">
            <ImageIcon className="h-5 w-5 text-neon-cyan" />
            媒體庫
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-gray-400 transition hover:bg-white/5 hover:text-white"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {message && (
          <div
            className={`mx-4 mt-4 flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium ${
              message.type === "err"
                ? "bg-neon-pink/10 text-neon-pink"
                : "bg-neon-cyan/10 text-neon-cyan"
            }`}
          >
            {message.type === "err" ? (
              <AlertCircle className="h-4 w-4" />
            ) : (
              <CheckCircle2 className="h-4 w-4" />
            )}
            {message.text}
          </div>
        )}

        <div className="border-b border-neon-cyan/15 p-4">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            accept="image/*,video/mp4"
            multiple
            className="hidden"
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="flex w-full flex-col items-center gap-2 rounded-xl border-2 border-dashed border-neon-cyan/30 py-6 text-sm text-gray-400 transition hover:border-neon-cyan hover:text-neon-cyan disabled:opacity-50"
          >
            {uploading ? (
              <>
                <Loader2 className="h-6 w-6 animate-spin" />
                <span>上傳中…</span>
              </>
            ) : (
              <>
                <Upload className="h-6 w-6" />
                <span>點擊上傳圖片或影片</span>
                <span className="text-xs text-gray-500">支援 JPG / PNG / WebP / GIF / MP4；圖片會自動壓縮至 1920px、品質 85%</span>
              </>
            )}
          </button>
          {compressInfo && <p className="mt-2 text-center text-xs text-neon-cyan">{compressInfo}</p>}
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-neon-cyan" />
            </div>
          ) : media.length === 0 ? (
            <div className="py-12 text-center text-gray-500">
              <ImageIcon className="mx-auto mb-3 h-12 w-12 opacity-50" />
              <p className="text-sm">尚無媒體檔案</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4">
              {media.map((item) => {
                const isVideo = /\.(mp4|webm|mov|mkv|ogv)$/i.test(item.key);
                return (
                  <div
                    key={item.key}
                    className="group relative overflow-hidden rounded-xl border border-neon-cyan/15 bg-deep-black hover:border-neon-cyan"
                  >
                    {isVideo ? (
                      <video
                        src={item.url}
                        muted
                        preload="metadata"
                        className="h-32 w-full cursor-pointer object-cover"
                        onClick={() => {
                          onSelect(item.url);
                          onClose();
                        }}
                      />
                    ) : (
                      <img
                        src={item.url}
                        alt={item.key}
                        className="h-32 w-full cursor-pointer object-cover"
                        onClick={() => {
                          onSelect(item.url);
                          onClose();
                        }}
                      />
                    )}
                    <div className="absolute left-2 top-2 rounded-md bg-deep-black/70 p-1">
                      {isVideo ? (
                        <Video className="h-3.5 w-3.5 text-neon-cyan" />
                      ) : (
                        <ImageIcon className="h-3.5 w-3.5 text-neon-cyan" />
                      )}
                    </div>
                    <div className="p-2">
                      <p className="truncate text-xs text-gray-500">{item.key}</p>
                      <p className="text-xs text-gray-600">{formatSize(item.size)}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleDelete(item.key)}
                      className="absolute right-2 top-2 rounded-lg bg-neon-pink p-1.5 text-white opacity-0 transition hover:bg-neon-pink/80 group-hover:opacity-100"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
